/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom } from 'jotai';
import { addOrUpdateEntityItem } from 'jotai/entity';
import { activeSeriesIdSelector } from 'jotai/session';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { activeWorkspaceAtom } from 'jotai/workspace';
import _ from 'lodash';
import { useCallback } from 'react';
import { workspaceService } from 'services/workspace/WorkspaceService';
import { getCompetitorGroupVehicleDefault } from './CompetitorGroupDefaultFactory';
import { CompetitorGroup, CompetitorGroupVehicle } from './CompetitorGroupObject';
import { VehicleSelectionGridObject } from 'components/common/VehicleSelectionGrid/VehicleSelectionGrid.type';
import { seriesUniqueVehicleNumbersSelectorFamily } from 'jotai/driver';
import { getDefaultVehicleColor } from 'jotai/vehicleColor';
import { v4 as uuidv4 } from 'uuid';

export const competitorGroupsOpenAtom = atom<boolean>(false);

const competitorGroupIdsAtom = atom<string[]>((get) => {
  const currentWorkspace = get(activeWorkspaceAtom);
  if (Object.keys(currentWorkspace).length > 0) {
    return [...currentWorkspace.workspace.competitorGroups.allIds];
  } else {
    return [];
  }
});

const competitorGroupAtomFamily = atomFamily<string, Atom<CompetitorGroup>>((id: string) =>
  atom<CompetitorGroup>((get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      const value = currentWorkspace.workspace.competitorGroups.byId[id];
      if (value) {
        return value;
      } else {
        return { id: '', name: '', seriesId: '', vehicles: [] };
      }
    } else {
      return { id: '', name: '', seriesId: '', vehicles: [] };
    }
  })
);

const competitorGroupVehicleIdsAtom = atom<string[]>((get) => {
  const currentWorkspace = get(activeWorkspaceAtom);
  if (Object.keys(currentWorkspace).length > 0) {
    return [...currentWorkspace.workspace.competitorGroupVehicles.allIds];
  } else {
    return [];
  }
});

const competitorGroupVehicleAtomFamily = atomFamily<string, Atom<CompetitorGroupVehicle>>((id: string) =>
  atom<CompetitorGroupVehicle>((get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      const value = currentWorkspace.workspace.competitorGroupVehicles.byId[id];
      if (value) {
        return value;
      } else {
        return getCompetitorGroupVehicleDefault(id);
      }
    } else {
      return getCompetitorGroupVehicleDefault(id);
    }
  })
);

export const competitorGroupVehiclesSelectorFamily = atomFamily<string, Atom<CompetitorGroupVehicle[]>>(
  (competitorGroupId: string) =>
    atom<CompetitorGroupVehicle[]>((get) => {
      const competitorGroup = get(competitorGroupAtomFamily(competitorGroupId));
      return competitorGroup.vehicles.map((id) => get(competitorGroupVehicleAtomFamily(id)));
    })
);

type CompetitorGroupVehicleParam = { vehicleNumber: string; competitorGroupId: string; seriesId?: string };

export const competitorGroupVehicleByVehicleNumberSelectorFamily = atomFamily<
  CompetitorGroupVehicleParam,
  Atom<CompetitorGroupVehicle>
>(
  (param: CompetitorGroupVehicleParam) =>
    atom<CompetitorGroupVehicle>((get) => {
      const vehicles = get(competitorGroupVehiclesSelectorFamily(param.competitorGroupId));
      const vehicle = vehicles.find((vehicle) => vehicle.vehicleNumber === param.vehicleNumber);
      const seriesId = param.seriesId ? param.seriesId : get(activeSeriesIdSelector);
      return vehicle || getCompetitorGroupVehicleDefault(undefined, param.vehicleNumber, false, seriesId);
    }),
  (a, b) => _.isEqual(a, b)
);

type CompetitorGroupVehicleAndSeriesIdParam = { vehicleNumber: string; seriesId: string };

export const competitorGroupVehicleByVehicleNumberAndSeriesIdSelectorFamily = atomFamily<
  CompetitorGroupVehicleAndSeriesIdParam,
  Atom<CompetitorGroupVehicle>
>(
  (param: CompetitorGroupVehicleAndSeriesIdParam) =>
    atom<CompetitorGroupVehicle>((get) => {
      const competitorGroupId = get(selectedCompetitorGroupIdAtomFamily(param.seriesId));
      if (!competitorGroupId) {
        return getCompetitorGroupVehicleDefault(undefined, param.vehicleNumber, false, param.seriesId);
      } else {
        const vehicles = get(competitorGroupVehiclesSelectorFamily(competitorGroupId));
        const vehicle = vehicles.find((vehicle) => vehicle.vehicleNumber === param.vehicleNumber);

        return vehicle || getCompetitorGroupVehicleDefault(undefined, param.vehicleNumber, false, param.seriesId);
      }
    }),
  (a, b) => _.isEqual(a, b)
);

export const competitorGroupBySeriesSelectorFamily = atomFamily<string, Atom<CompetitorGroup | undefined>>(
  (seriesId: string) =>
    atom<CompetitorGroup | undefined>((get) => {
      const competitorGroupsIds = get(competitorGroupIdsAtom);
      const competitorGroups = competitorGroupsIds.map((id) => {
        return get(competitorGroupAtomFamily(id));
      });
      const filtered = competitorGroups.filter((competitorGroup) => competitorGroup.seriesId === seriesId);
      const selectedCompetitorGroupId = get(selectedCompetitorGroupIdAtomFamily(seriesId));
      const selectedCompitorGroups = filtered.find((item) => item.id === selectedCompetitorGroupId);
      return selectedCompitorGroups || filtered[0];
    })
);

const selectedCompetitorGroupIdAtomFamily = atomFamily<string, Atom<string | undefined>>((seriesId: string) =>
  atom<string | undefined>((get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      return currentWorkspace.workspace.selectedCompetitorGroups[seriesId];
    } else {
      return undefined;
    }
  })
);

export const activeCompetitorGroupVehiclesSelector = atom<CompetitorGroupVehicle[]>((get) => {
  let competitorGroup;
  const seriesId = get(activeSeriesIdSelector);
  if (seriesId) {
    const competitorGroupId = get(selectedCompetitorGroupIdAtomFamily(seriesId));
    if (competitorGroupId) {
      competitorGroup = get(competitorGroupAtomFamily(competitorGroupId));
    }
  }
  return competitorGroup ? get(competitorGroupVehiclesSelectorFamily(competitorGroup.id)) : [];
});

export const activeCompetitorGroupHighlightedVehiclesSelector = atom<CompetitorGroupVehicle[]>((get) => {
  return get(activeCompetitorGroupVehiclesSelector).filter((vehicle) => vehicle.highlighted);
});

export const activeCompetitorGroupIdSelector = atom<string | undefined>((get) => {
  const seriesId = get(activeSeriesIdSelector);
  if (seriesId) {
    return get(selectedCompetitorGroupIdAtomFamily(seriesId));
  }
});

export const useUpsertCompetitorGroup = () => {
  return useAtomCallback(
    useCallback((get, set, competitorGroup: CompetitorGroup, isSelected: boolean = false) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      addOrUpdateEntityItem(currentWorkspace.workspace.competitorGroups, competitorGroup, competitorGroup.id);
      if (isSelected) {
        currentWorkspace.workspace.selectedCompetitorGroups[competitorGroup.seriesId] = competitorGroup.id;
      }
      set(activeWorkspaceAtom, { ...currentWorkspace });
      workspaceService.upsertCompetitorGroup(competitorGroup, isSelected);
    }, [])
  );
};

export const useUpsertCompetitorGroupVehicles = () => {
  return useAtomCallback(
    useCallback(
      (
        get,
        set,
        competitorGroupId: string,
        competitorGroupVehicle: CompetitorGroupVehicle | CompetitorGroupVehicle[]
      ) => {
        let competitorGroupVehicleIds = get(competitorGroupVehicleIdsAtom);
        const competitorGroup = get(competitorGroupAtomFamily(competitorGroupId));
        let competitorGroupVehicles = [...competitorGroup.vehicles];

        const updateVehicle = (vehicle: CompetitorGroupVehicle) => {
          competitorGroupVehicleIds = [...competitorGroupVehicleIds.filter((id) => id !== vehicle.id), vehicle.id];

          if (!competitorGroupVehicles.includes(vehicle.id)) {
            competitorGroupVehicles = [...competitorGroupVehicles, vehicle.id];
          }
        };

        if (Array.isArray(competitorGroupVehicle)) {
          competitorGroupVehicle.forEach((vehicle) => updateVehicle(vehicle));
        } else {
          updateVehicle(competitorGroupVehicle);
        }

        const newCompetitorGroup = {
          ...competitorGroup,
          vehicles: [...competitorGroupVehicles],
        };

        const workspace = get(activeWorkspaceAtom);
        // Update competitor group (vehicles).
        addOrUpdateEntityItem(workspace.workspace.competitorGroups, newCompetitorGroup, newCompetitorGroup.id);
        // Update the vehicles.
        const vehicles = Array.isArray(competitorGroupVehicle) ? competitorGroupVehicle : [competitorGroupVehicle];
        vehicles.forEach((vehicle) =>
          addOrUpdateEntityItem(workspace.workspace.competitorGroupVehicles, vehicle, vehicle.id)
        );
        set(activeWorkspaceAtom, { ...workspace });
      },
      []
    )
  );
};

export const allCompetitorGroupVehiclesAtom = atom<VehicleSelectionGridObject[]>((get) => {
  const activeSeriesId = get(activeSeriesIdSelector);
  if (activeSeriesId) {
    const competitorGroup = get(competitorGroupBySeriesSelectorFamily(activeSeriesId)) ?? {
      id: uuidv4(),
      name: '',
      seriesId: activeSeriesId,
      vehicles: [],
    };
    const allVehicleNumbers = get(seriesUniqueVehicleNumbersSelectorFamily(activeSeriesId));
    const vehicles = get(competitorGroupVehiclesSelectorFamily(competitorGroup.id));
    const competitorGroupVehicles = allVehicleNumbers.map((vehicleNumber) => {
      const item = {
        vehicleNumber,
        colorId: getDefaultVehicleColor(vehicleNumber, activeSeriesId).id,
      } as VehicleSelectionGridObject;
      const vehicle = vehicles.find((vehicle) => vehicle.vehicleNumber === vehicleNumber);
      if (vehicle) {
        item.selected = vehicle.highlighted;
        item.colorId = vehicle.colorId || item.colorId;
      }
      return item;
    });
    return competitorGroupVehicles;
  } else {
    return [];
  }
});

export const activeHighlightedCompetitorGroupVehicleNumbersSelector = atom<string[]>((get) => {
  return get(activeCompetitorGroupHighlightedVehiclesSelector).map((vehicle) => vehicle.vehicleNumber);
});
